/**
 * Common API Service for AdRocket
 * Base URL: http://localhost/adrocket-api/
 */

const API_BASE_URL = 'https://adrocketagency.com/api/';


/**
 * Common API request handler
 * @param {string} endpoint - API endpoint (e.g., 'contact', 'career')
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} data - Request payload
 * @param {Object} options - Additional options (headers, etc.)
 * @returns {Promise} - API response
 */
const apiRequest = async (endpoint, method = 'POST', data = null, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config = {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        },
        ...options
    };

    // Add body for POST/PUT requests
    if (data && (method === 'POST' || method === 'PUT')) {
        // If FormData is provided, don't set Content-Type (browser will set it with boundary)
        if (data instanceof FormData) {
            delete config.headers['Content-Type'];
            config.body = data;
        } else {
            config.body = JSON.stringify(data);
        }
    }

    try {
        const response = await fetch(url, config);
        
        // Check if response is ok first
        if (!response.ok) {
            // Try to parse as JSON first
            let result;
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                result = await response.json();
            } else {
                // If not JSON (e.g., HTML error page), read as text
                const text = await response.text();
                
                // Handle specific HTTP errors
                if (response.status === 403) {
                    return {
                        success: false,
                        error: 'Access forbidden. Please check server permissions and API endpoint configuration.',
                        status: response.status
                    };
                } else if (response.status === 404) {
                    return {
                        success: false,
                        error: 'API endpoint not found. Please verify the endpoint URL.',
                        status: response.status
                    };
                } else if (response.status === 500) {
                    return {
                        success: false,
                        error: 'Server error. Please try again later.',
                        status: response.status
                    };
                } else {
                    return {
                        success: false,
                        error: `HTTP error! Status: ${response.status}`,
                        status: response.status
                    };
                }
            }
            
            // Handle API error responses (JSON)
            const errorMessage = result.message || `HTTP error! status: ${response.status}`;
            const errors = result.errors || [];
            
            return {
                success: false,
                error: errorMessage,
                errors: errors,
                status: response.status
            };
        }

        // Parse JSON response for successful requests
        const result = await response.json().catch(() => {
            return {
                success: false,
                message: 'Invalid JSON response from server'
            };
        });

        // Success response
        return {
            success: result.success !== false, // Default to true if not specified
            data: result,
            message: result.message
        };
    } catch (error) {
        console.error('API Error:', error);
        
        // Handle network errors
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            return {
                success: false,
                error: 'Network error. Please check your connection and ensure the API server is running.'
            };
        }
        
        return {
            success: false,
            error: error.message || 'An error occurred while processing your request'
        };
    }
};


export const submitContactForm = async (formData) => {
    const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        purpose: formData.purpose,
        ...(formData.subject && { subject: formData.subject }),
        ...(formData.budget && { budget: formData.budget }),
        ...(formData.message && { message: formData.message })
    };

    return await apiRequest('contact', 'POST', payload);
};

export const submitCareerForm = async (formData) => {
    // Create FormData for file upload (multipart/form-data)
    const formDataToSend = new FormData();
    
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('subject', formData.subject);
    
    // Append resume file (required)
    if (formData.resume) {
        formDataToSend.append('resume', formData.resume);
    }
    
    // Append message if provided (optional)
    if (formData.message && formData.message.trim()) {
        formDataToSend.append('message', formData.message);
    }

    return await apiRequest('careers', 'POST', formDataToSend);
};

/**
 * Subscribe to Newsletter
 * @param {string} email - Email address for newsletter subscription
 * @returns {Promise} - API response
 */
export const subscribeNewsletter = async (email) => {
    const payload = {
        email: email
    };

    return await apiRequest('newsletter', 'POST', payload);
};

/**
 * Fetch Testimonials
 * @returns {Promise} - API response with testimonials data
 */
export const fetchTestimonials = async () => {
    return await apiRequest('testimonials', 'GET');
};

export default {
    submitContactForm,
    submitCareerForm,
    subscribeNewsletter,
    fetchTestimonials,
    apiRequest
};

